import { observer } from "mobx-react-lite";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { TabView, TabPanel } from "primereact/tabview";
import { Card } from "primereact/card";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import React, { useEffect, useRef, useState, useCallback } from "react";
import SectionHeading from "../../app/common/SectionHeading/SectionHeading";
import agent from "../../app/api/agent";

// ===================== Prior Model Categorization =====================
// ===================== Prior Model Categorization =====================
// Each model type requires specific input parameters and endpoint:
//
//   Reinvent (RNN)        → no input needed               → POST /generate
//   Mol2Mol (transformer) → seed SMILES required           → POST /generate + smiles_input
//   Libinvent (RNN/v2)    → scaffold + attachment points   → POST /scaffold
//   Linkinvent (RNN/v2)   → warhead SMILES required        → POST /generate + smiles_input
//   Pepinvent (transformer) → peptide input required       → POST /generate + smiles_input
//
const MODEL_TYPE = "MISSING"; // placeholder — actual type is loaded from prior file metadata
const PRIOR_CATEGORIES = {
  generate: ["reinvent.prior"],  // De novo: RNN-based, no seed needed
  scaffold: [                     // Scaffold Hopping: Mol2Mol transformer, needs seed SMILES
    "mol2mol_scaffold.prior",
    "mol2mol_scaffold_generic.prior",
    "mol2mol_similarity.prior",
    "mol2mol_high_similarity.prior",
    "mol2mol_medium_similarity.prior",
    "mol2mol_mmp.prior",
    "pubchem_ecfp4_with_count_with_rank_reinvent4_dict_voc.prior",
  ],
  rgroup: [                       // R-Group: Libinvent (uses /scaffold endpoint)
    "libinvent.prior",
    "libinvent_transformer_pubchem.prior",
  ],
  linker: [                       // Linker Design: Linkinvent, needs warhead SMILES
    "linkinvent.prior",
    "linkinvent_transformer_pubchem.prior",
  ],
  peptide: ["pepinvent.prior"],  // Peptide: Pepinvent transformer, needs peptide input
  optimize: ["reinvent.prior"],  // RL Optimization: RNN-based only
};

const ALL_PRIORS = Object.values(PRIOR_CATEGORIES).flat();

// ===================== Helpers =====================

function smilesToFormula(smiles) {
  try {
    const elements = {};
    const re = /([A-Z][a-z]?)(\d*)/g;
    const clean = smiles.replace(/\[|\]|\d|[#@+\-\\\/%=\(\)\.:]/g, " ").replace(/\s+/g, " ");
    const atoms = clean.trim().split(" ");
    for (const a of atoms) {
      if (!a || a.length === 0) continue;
      const m = a.match(re);
      if (m) {
        for (const part of m) {
          const match = part.match(re);
          if (match) {
            const el = match[1];
            const cnt = parseInt(match[2]) || 1;
            elements[el] = (elements[el] || 0) + cnt;
          }
        }
      }
    }
    const order = ["C", "H", "O", "N", "S", "P", "F", "Cl", "Br", "I", "B", "Si", "Se", "Te"];
    let formula = "";
    for (const el of order) {
      if (elements[el]) {
        formula += el + (elements[el] > 1 ? elements[el] : "");
        delete elements[el];
      }
    }
    const remaining = Object.keys(elements).sort();
    for (const el of remaining) {
      if (elements[el]) {
        formula += el + (elements[el] > 1 ? elements[el] : "");
      }
    }
    return formula;
  } catch {
    return "N/A";
  }
}

// SMILES 2D renderer — using canvas-based Drawer (matching compound list approach)
const SmilesRenderer = ({ smiles, width = 120, height = 80 }) => {
  const canId = `smiles-canvas-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
  const [dialogVisible, setDialogVisible] = useState(false);
  const bigCanId = `smiles-big-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

  useEffect(() => {
    if (!smiles) return;
    try {
      const SmilesDrawer = require("smiles-drawer");
      const drawer = new SmilesDrawer.Drawer({ width, height, bondThickness: 1.0 });
      SmilesDrawer.parse(
        smiles,
        function (tree) {
          drawer.draw(tree, canId, "light", false);
        },
        function (err) {
          console.error("SmilesDrawer parse error:", err);
        }
      );
    } catch (e) {
      console.error("Failed to draw SMILES:", e);
    }
  }, [smiles, width, height]);

  // Draw the big version when dialog opens
  useEffect(() => {
    if (!dialogVisible || !smiles) return;
    // Short delay to ensure the dialog DOM is rendered
    const timer = setTimeout(() => {
      try {
        const SmilesDrawer = require("smiles-drawer");
        const bigDrawer = new SmilesDrawer.Drawer({ width: 350, height: 280, bondThickness: 1.5 });
        SmilesDrawer.parse(
          smiles,
          function (tree) {
            bigDrawer.draw(tree, bigCanId, "light", false);
          },
          function (err) {
            console.error("Big smiles parse error:", err);
          }
        );
      } catch (e) {
        console.error("Failed to draw big SMILES:", e);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [dialogVisible, smiles, bigCanId]);

  return React.createElement(React.Fragment, null,
    React.createElement("div", {
      style: { cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center", minWidth: width, minHeight: height },
      onClick: () => setDialogVisible(true)
    }, React.createElement("canvas", { id: canId })),
    React.createElement(Dialog, {
      header: "Molecular Structure",
      visible: dialogVisible,
      onHide: () => setDialogVisible(false),
      style: { width: "450px" },
      dismissableMask: true
    },
      React.createElement("div", { className: "flex flex-column align-items-center", style: { gap: "1rem" } },
        React.createElement("canvas", { id: bigCanId }),
        React.createElement("div", { className: "flex flex-column", style: { gap: "0.3rem", width: "100%" } },
          React.createElement("div", { className: "flex" },
            React.createElement("strong", { style: { minWidth: "80px" } }, "SMILES:"),
            React.createElement("span", { style: { fontFamily: "monospace", fontSize: "0.85rem", wordBreak: "break-all" } }, smiles)
          )
        )
      )
    )
  );
};

// NLL severity tag
const NllTag = ({ value }) => {
  const nll = parseFloat(value);
  let severity = "success";
  if (nll > 30) severity = "danger";
  else if (nll > 25) severity = "warn";
  return React.createElement(Tag, { value: nll.toFixed(2), severity });
};

// Status tracker
const TaskStatus = ({ taskId, generating, results }) => {
  if (generating) {
    return React.createElement("div", { className: "flex flex-column", style: { gap: "0.5rem" } },
      React.createElement("p", { className: "text-sm text-600" }, `Task: ${taskId ? taskId.substring(0, 8) + "..." : "starting..."}`),
      React.createElement(ProgressBar, { mode: "indeterminate", style: { height: "6px" } }),
      React.createElement("p", { className: "text-sm text-600" }, "Generating molecules...")
    );
  }
  if (results.length > 0) {
    return React.createElement("p", { className: "text-green-600 font-bold" }, `\u2713 ${results.length} molecules generated`);
  }
  return React.createElement("p", { className: "text-600" }, "Configure parameters and click Generate");
};

// ===================== Common Results Table with Batch Selector =====================
const ResultsTable = ({ batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch, generating }) => {
  const openPubChem = (smiles) => {
    const url = `https://pubchem.ncbi.nlm.nih.gov/#query=${encodeURIComponent(smiles)}&tab=similarity`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const smilesTemplate = (rowData) =>
    React.createElement("span", {
      style: { fontFamily: "monospace", fontSize: "0.8rem", wordBreak: "break-all", cursor: "pointer", color: "var(--primary-color)" },
      onClick: (e) => { e.stopPropagation(); openPubChem(rowData.SMILES); },
      title: "Click to view on PubChem"
    }, rowData.SMILES);
  const structureTemplate = (rowData) => React.createElement(SmilesRenderer, { smiles: rowData.SMILES, width: 120, height: 70 });
  const nllTemplate = (rowData) => React.createElement(NllTag, { value: rowData.NLL });
  const formulaTemplate = (rowData) => {
    const formula = smilesToFormula(rowData.SMILES);
    return React.createElement("span", { style: { fontFamily: "monospace", fontSize: "0.85rem" } }, formula);
  };

  // No batches yet
  if (batches.length === 0) {
    return React.createElement("p", { className: "text-600" }, generating
      ? React.createElement(ProgressBar, { mode: "indeterminate", style: { height: "6px" } })
      : "Configure parameters and generate. Results will appear here.");
  }

  // Batch selector options
  const batchOptions = batches.map((b, i) => ({
    label: `#${i + 1}: ${b.label}`,
    value: i,
  }));
  const currentBatch = batches[selectedBatchIndex] || batches[batches.length - 1];
  const currentResults = currentBatch.results || [];

  return React.createElement("div", { className: "flex flex-column" },
    // Batch selector bar
    React.createElement("div", { className: "flex align-items-center justify-content-between mb-2", style: { gap: "0.5rem" } },
      React.createElement("div", { className: "flex align-items-center", style: { gap: "0.5rem" } },
        React.createElement("label", { className: "text-600 font-medium", style: { fontSize: "0.85rem", whiteSpace: "nowrap" } }, "Batches:"),
        React.createElement("div", { className: "flex", style: { gap: "0.25rem" } },
          ...batches.map((b, i) =>
            React.createElement(Button, {
              key: b.id,
              label: `#${i + 1}`,
              tooltip: `${b.label} — ${b.timestamp} (${b.results.length} mols)`,
              tooltipOptions: { position: "top" },
              className: i === selectedBatchIndex
                ? "p-button-sm p-button-primary"
                : "p-button-sm p-button-outlined",
              onClick: () => onSelectBatch(i),
              style: { fontSize: "0.8rem" }
            })
          ),
          batches.length > 1 && React.createElement(Button, {
            icon: "pi pi-trash",
            className: "p-button-sm p-button-danger p-button-text",
            tooltip: "Delete selected batch",
            tooltipOptions: { position: "top" },
            onClick: (e) => {
              e.stopPropagation();
              if (window.confirm(`Delete batch #${selectedBatchIndex + 1}?`)) {
                onDeleteBatch(selectedBatchIndex);
              }
            },
            style: { fontSize: "0.8rem", marginLeft: "0.25rem" }
          })
        )
      ),
      React.createElement("div", { className: "flex align-items-center", style: { gap: "0.5rem" } },
        React.createElement("span", { className: "text-600", style: { fontSize: "0.85rem" } },
          `${currentResults.length} molecules`
        ),
        React.createElement(Button, {
          label: "Download CSV", icon: "pi pi-download",
          className: "p-button-sm p-button-success",
          onClick: () => onDownloadBatch(selectedBatchIndex),
          disabled: currentResults.length === 0
        })
      )
    ),
    // Batch info
    React.createElement("div", { className: "mb-2", style: { fontSize: "0.8rem", color: "var(--text-color-secondary)" } },
      `${currentBatch.timestamp} — ${currentBatch.label}`
    ),
    // Results table for selected batch
    React.createElement(DataTable, {
      value: currentResults, paginator: true, rows: 15,
      className: "w-full", emptyMessage: "No molecules in this batch.",
      sortField: "NLL", sortOrder: 1, size: "small", stripedRows: true
    },
      React.createElement(Column, { field: "SMILES", header: "SMILES", body: smilesTemplate, sortable: true, style: { minWidth: "200px" } }),
      React.createElement(Column, { header: "Structure", body: structureTemplate, style: { width: "140px" } }),
      React.createElement(Column, { field: "NLL", header: "NLL", body: nllTemplate, sortable: true, style: { width: "100px" } }),
      React.createElement(Column, { header: "Formula", body: formulaTemplate, style: { width: "130px" } })
    )
  );
};

// ===================== Tab Panels =====================

// Tab 0: Generate
const GenerateTab = ({ priors, selectedPrior, setSelectedPrior, numSmiles, setNumSmiles, uniqueMols, setUniqueMols, randomize, setRandomize, handleGenerate, generating, batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch }) => {
  // Derive current results for TaskStatus
  const currentTabResults = (batches && batches.length > 0)
    ? batches[selectedBatchIndex >= 0 ? selectedBatchIndex : batches.length - 1].results
    : [];
  return React.createElement("div", { className: "flex", style: { gap: "1rem", flexWrap: "wrap" } },
    React.createElement(Card, { title: "Parameters", className: "flex-1", style: { minWidth: "300px", maxWidth: "400px" } },
      React.createElement("div", { className: "flex flex-column", style: { gap: "1rem" } },
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "prior", className: "mb-1 text-600 font-medium" }, "Prior Model"),
          React.createElement(Dropdown, {
            id: "prior", value: selectedPrior, options: priors,
            onChange: (e) => setSelectedPrior(e.value), placeholder: "Select a prior model", className: "w-full"
          })
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "numSmiles", className: "mb-1 text-600 font-medium" }, "Number of SMILES"),
          React.createElement(InputNumber, {
            id: "numSmiles", value: numSmiles, onValueChange: (e) => setNumSmiles(e.value || 10), min: 1, max: 500, className: "w-full"
          })
        ),
        // De novo generation (Reinvent RNN) — no seed SMILES needed
        React.createElement("hr", { className: "my-1" }),
        React.createElement("p", { className: "text-600 font-medium mb-1", style: { fontSize: "0.9rem" } }, "Advanced"),
        React.createElement("div", { className: "flex align-items-center" },
          React.createElement(InputSwitch, { checked: uniqueMols, onChange: (e) => setUniqueMols(e.value) }),
          React.createElement("label", { className: "ml-2 text-600", style: { fontSize: "0.85rem" } }, "Unique molecules only")
        ),
        React.createElement("div", { className: "flex align-items-center" },
          React.createElement(InputSwitch, { checked: randomize, onChange: (e) => setRandomize(e.value) }),
          React.createElement("label", { className: "ml-2 text-600", style: { fontSize: "0.85rem" } }, "Randomize SMILES")
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement(Button, {
            label: generating ? "Generating..." : "Generate Molecules",
            icon: generating ? "pi pi-spin pi-spinner" : "pi pi-play",
            onClick: handleGenerate, disabled: generating || !selectedPrior,
            className: "w-full p-button-lg"
          })
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement("p", { className: "text-600 font-medium mb-2" }, "Status"),
          React.createElement(TaskStatus, { taskId: batches.length > 0 ? "done" : null, generating, results: currentTabResults })
        )
      )
    ),
    React.createElement(Card, { title: "Generated Results", className: "flex-3", style: { minWidth: "500px" } },
      React.createElement(ResultsTable, { batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch, generating })
    )
  );
};

// Tab 1: Scaffold Hopping (Mol2Mol) — uses /generate + smiles_input
const ScaffoldHoppingTab = ({ priors, selectedPrior, setSelectedPrior, scaffoldSmiles, setScaffoldSmiles, numScaffold, setNumScaffold, handleScaffold, generating, batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch }) => {
  const currentTabResults = (batches && batches.length > 0)
    ? batches[selectedBatchIndex >= 0 ? selectedBatchIndex : batches.length - 1].results
    : [];
  return React.createElement("div", { className: "flex", style: { gap: "1rem", flexWrap: "wrap" } },
    React.createElement(Card, { title: "Parameters", className: "flex-1", style: { minWidth: "300px", maxWidth: "400px" } },
      React.createElement("div", { className: "flex flex-column", style: { gap: "1rem" } },
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "priorScf", className: "mb-1 text-600 font-medium" }, "Prior Model"),
          React.createElement(Dropdown, {
            id: "priorScf", value: selectedPrior, options: priors,
            onChange: (e) => setSelectedPrior(e.value), placeholder: "Select a scaffold model", className: "w-full"
          })
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "seedSmilesScf", className: "mb-1 text-600 font-medium" }, "Seed SMILES"),
          React.createElement(InputTextarea, {
            id: "seedSmilesScf", value: scaffoldSmiles, onChange: (e) => setScaffoldSmiles(e.target.value),
            rows: 3, placeholder: "Enter seed molecule SMILES, one per line\ne.g.\nC1=CC=CC=C1\nC1CCCCC1",
            className: "w-full", autoResize: true
          }),
          React.createElement("small", { className: "text-500" }, "Mol2Mol generates variants of these seed molecules")
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "numScf", className: "mb-1 text-600 font-medium" }, "Molecules per scaffold"),
          React.createElement(InputNumber, {
            id: "numScf", value: numScaffold, onValueChange: (e) => setNumScaffold(e.value || 10), min: 1, max: 100, className: "w-full"
          })
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement(Button, {
            label: generating ? "Generating..." : "Run Scaffold Hopping",
            icon: generating ? "pi pi-spin pi-spinner" : "pi pi-sitemap",
            onClick: handleScaffold, disabled: generating || !selectedPrior || !scaffoldSmiles.trim(),
            className: "w-full p-button-lg p-button-info"
          })
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement("p", { className: "text-600 font-medium mb-2" }, "Status"),
          React.createElement(TaskStatus, { taskId: batches.length > 0 ? "done" : null, generating, results: currentTabResults })
        )
      )
    ),
    React.createElement(Card, { title: "Scaffold Hopping Results", className: "flex-3", style: { minWidth: "500px" } },
      React.createElement(ResultsTable, { batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch, generating })
    )
  );
};

// Tab 2: R-Group (LibInvent) — uses /scaffold endpoint
const RGroupTab = ({ priors, selectedPrior, setSelectedPrior, rgroupSmiles, setRgroupSmiles, numRgroup, setNumRgroup, handleRGroup, generating, batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch }) => {
  const currentTabResults = (batches && batches.length > 0)
    ? batches[selectedBatchIndex >= 0 ? selectedBatchIndex : batches.length - 1].results
    : [];
  return React.createElement("div", { className: "flex", style: { gap: "1rem", flexWrap: "wrap" } },
    React.createElement(Card, { title: "Parameters", className: "flex-1", style: { minWidth: "300px", maxWidth: "400px" } },
      React.createElement("div", { className: "flex flex-column", style: { gap: "1rem" } },
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "priorRg", className: "mb-1 text-600 font-medium" }, "Prior Model"),
          React.createElement(Dropdown, {
            id: "priorRg", value: selectedPrior, options: priors,
            onChange: (e) => setSelectedPrior(e.value), placeholder: "Select a model", className: "w-full"
          })
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "rgroupSmiles", className: "mb-1 text-600 font-medium" }, "Scaffold SMILES"),
          React.createElement(InputTextarea, {
            id: "rgroupSmiles", value: rgroupSmiles, onChange: (e) => setRgroupSmiles(e.target.value),
            rows: 3, placeholder: "Enter scaffold SMILES with attachment points (*)\ne.g.\nCC(C)(C)c1ccc2c(c1)C(=O)N(*)C2=O",
            className: "w-full", autoResize: true
          }),
          React.createElement("small", { className: "text-500" }, "Use * for attachment points where R-groups attach")
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "numRg", className: "mb-1 text-600 font-medium" }, "Molecules per scaffold"),
          React.createElement(InputNumber, {
            id: "numRg", value: numRgroup, onValueChange: (e) => setNumRgroup(e.value || 10), min: 1, max: 100, className: "w-full"
          })
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement(Button, {
            label: generating ? "Generating..." : "Add R-Groups",
            icon: generating ? "pi pi-spin pi-spinner" : "pi pi-pencil",
            onClick: handleRGroup, disabled: generating || !selectedPrior || !rgroupSmiles.trim(),
            className: "w-full p-button-lg p-button-info"
          })
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement("p", { className: "text-600 font-medium mb-2" }, "Status"),
          React.createElement(TaskStatus, { taskId: batches.length > 0 ? "done" : null, generating, results: currentTabResults })
        )
      )
    ),
    React.createElement(Card, { title: "R-Group Results", className: "flex-3", style: { minWidth: "500px" } },
      React.createElement(ResultsTable, { batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch, generating })
    )
  );
};

// Tab 3: Linker
const LinkerTab = ({ priors, selectedPrior, setSelectedPrior, linkerSmiles, setLinkerSmiles, numLinker, setNumLinker, handleLinker, generating, batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch }) => {
  const currentTabResults = (batches && batches.length > 0)
    ? batches[selectedBatchIndex >= 0 ? selectedBatchIndex : batches.length - 1].results
    : [];
  return React.createElement("div", { className: "flex", style: { gap: "1rem", flexWrap: "wrap" } },
    React.createElement(Card, { title: "Parameters", className: "flex-1", style: { minWidth: "300px", maxWidth: "400px" } },
      React.createElement("div", { className: "flex flex-column", style: { gap: "1rem" } },
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "priorLink", className: "mb-1 text-600 font-medium" }, "Linker Model"),
          React.createElement(Dropdown, {
            id: "priorLink", value: selectedPrior, options: priors,
            onChange: (e) => setSelectedPrior(e.value), placeholder: "Select a linker model", className: "w-full"
          })
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "linkerSmiles", className: "mb-1 text-600 font-medium" }, "Warhead SMILES"),
          React.createElement(InputTextarea, {
            id: "linkerSmiles", value: linkerSmiles, onChange: (e) => setLinkerSmiles(e.target.value),
            rows: 3, placeholder: "Enter warhead SMILES, one per line\ne.g.\nc1ccccc1\nc1ccccn1\nUse * for attachment points",
            className: "w-full", autoResize: true
          }),
          React.createElement("small", { className: "text-500" }, "LinkInvent generates linkers between these warheads")
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "numLink", className: "mb-1 text-600 font-medium" }, "Linkers per fragment pair"),
          React.createElement(InputNumber, {
            id: "numLink", value: numLinker, onValueChange: (e) => setNumLinker(e.value || 10), min: 1, max: 100, className: "w-full"
          })
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement(Button, {
            label: generating ? "Generating..." : "Generate Linkers",
            icon: generating ? "pi pi-spin pi-spinner" : "pi pi-link",
            onClick: handleLinker, disabled: generating || !selectedPrior || !linkerSmiles.trim(),
            className: "w-full p-button-lg p-button-warning"
          })
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement("p", { className: "text-600 font-medium mb-2" }, "Status"),
          React.createElement(TaskStatus, { taskId: batches.length > 0 ? "done" : null, generating, results: currentTabResults })
        )
      )
    ),
    React.createElement(Card, { title: "Linker Results", className: "flex-3", style: { minWidth: "500px" } },
      React.createElement(ResultsTable, { batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch, generating })
    )
  );
};

// Tab 4: Peptide
const PeptideTab = ({ priors, selectedPrior, setSelectedPrior, numPeptide, setNumPeptide, peptideInput, setPeptideInput, handlePeptide, generating, batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch }) => {
  const currentTabResults = (batches && batches.length > 0)
    ? batches[selectedBatchIndex >= 0 ? selectedBatchIndex : batches.length - 1].results
    : [];
  return React.createElement("div", { className: "flex", style: { gap: "1rem", flexWrap: "wrap" } },
    React.createElement(Card, { title: "Parameters", className: "flex-1", style: { minWidth: "300px", maxWidth: "400px" } },
      React.createElement("div", { className: "flex flex-column", style: { gap: "1rem" } },
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "priorPep", className: "mb-1 text-600 font-medium" }, "Peptide Model"),
          React.createElement(Dropdown, {
            id: "priorPep", value: selectedPrior, options: priors,
            onChange: (e) => setSelectedPrior(e.value), placeholder: "Select a peptide model", className: "w-full"
          })
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "peptideInput", className: "mb-1 text-600 font-medium" }, "Peptide Sequences"),
          React.createElement(InputTextarea, {
            id: "peptideInput", value: peptideInput, onChange: (e) => setPeptideInput(e.target.value),
            rows: 3, placeholder: "Enter peptide sequences, one per line\ne.g.\nAla-Gly-Phe\nLeu-Enkephalin\nOr just SMILES for masked peptides",
            className: "w-full", autoResize: true
          }),
          React.createElement("small", { className: "text-500" }, "PepInvent extends/modifies these peptide sequences")
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "numPep", className: "mb-1 text-600 font-medium" }, "Number of Peptides"),
          React.createElement(InputNumber, {
            id: "numPep", value: numPeptide, onValueChange: (e) => setNumPeptide(e.value || 10), min: 1, max: 200, className: "w-full"
          })
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement(Button, {
            label: generating ? "Generating..." : "Generate Peptides",
            icon: generating ? "pi pi-spin pi-spinner" : "pi pi-star",
            onClick: handlePeptide, disabled: generating || !selectedPrior,
            className: "w-full p-button-lg p-button-help"
          })
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement("p", { className: "text-600 font-medium mb-2" }, "Status"),
          React.createElement(TaskStatus, { taskId: batches.length > 0 ? "done" : null, generating, results: currentTabResults })
        )
      )
    ),
    React.createElement(Card, { title: "Peptide Design Results", className: "flex-3", style: { minWidth: "500px" } },
      React.createElement(ResultsTable, { batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch, generating })
    )
  );
};

// Tab 4: Optimize (RL Staged Learning)
const OptimizeTab = ({ priors, selectedPrior, setSelectedPrior, batchSize, setBatchSize, maxSteps, setMaxSteps, maxScore, setMaxScore, handleOptimize, generating, batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch }) => {
  const currentTabResults = (batches && batches.length > 0)
    ? batches[selectedBatchIndex >= 0 ? selectedBatchIndex : batches.length - 1].results
    : [];
  return React.createElement("div", { className: "flex", style: { gap: "1rem", flexWrap: "wrap" } },
    React.createElement(Card, { title: "Optimization Parameters", className: "flex-1", style: { minWidth: "300px", maxWidth: "400px" } },
      React.createElement("div", { className: "flex flex-column", style: { gap: "1rem" } },
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "priorOpt", className: "mb-1 text-600 font-medium" }, "Prior Model"),
          React.createElement(Dropdown, {
            id: "priorOpt", value: selectedPrior, options: priors,
            onChange: (e) => setSelectedPrior(e.value), placeholder: "Select a prior model", className: "w-full"
          })
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "batchSize", className: "mb-1 text-600 font-medium" }, "Batch Size"),
          React.createElement(InputNumber, {
            id: "batchSize", value: batchSize, onValueChange: (e) => setBatchSize(e.value || 64), min: 1, max: 512, className: "w-full"
          }),
          React.createElement("small", { className: "text-500" }, "Molecules per RL batch (default: 64)")
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "maxSteps", className: "mb-1 text-600 font-medium" }, "Max Steps"),
          React.createElement(InputNumber, {
            id: "maxSteps", value: maxSteps, onValueChange: (e) => setMaxSteps(e.value || 100), min: 1, max: 1000, className: "w-full"
          }),
          React.createElement("small", { className: "text-500" }, "Maximum RL steps per stage (default: 100)")
        ),
        React.createElement("div", { className: "flex flex-column" },
          React.createElement("label", { htmlFor: "maxScore", className: "mb-1 text-600 font-medium" }, "Target Score"),
          React.createElement(InputNumber, {
            id: "maxScore", value: maxScore, onValueChange: (e) => setMaxScore(e.value || 0.6),
            min: 0, max: 1, step: 0.05, className: "w-full", mode: "decimal", minFractionDigits: 2, maxFractionDigits: 2
          }),
          React.createElement("small", { className: "text-500" }, "Stop when score exceeds this (0.0 - 1.0, default: 0.6)")
        ),
        React.createElement("hr", { className: "my-1" }),
        React.createElement("p", { className: "text-600 font-medium mb-1", style: { fontSize: "0.9rem" } }, "Default Scoring"),
        React.createElement("div", { className: "flex flex-column", style: { gap: "0.3rem", fontSize: "0.85rem", color: "var(--text-color-secondary)" } },
          React.createElement("span", null, "\u2022 Molecular Weight (200-500 Da)"),
          React.createElement("span", null, "\u2022 Unwanted SMARTS filter")
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement(Button, {
            label: generating ? "Optimizing..." : "Start RL Optimization",
            icon: generating ? "pi pi-spin pi-spinner" : "pi pi-chart-line",
            onClick: handleOptimize, disabled: generating || !selectedPrior,
            className: "w-full p-button-lg p-button-success"
          })
        ),
        React.createElement("div", { className: "mt-2" },
          React.createElement("p", { className: "text-600 font-medium mb-2" }, "Status"),
          React.createElement(TaskStatus, { taskId: batches.length > 0 ? "done" : null, generating, results: currentTabResults })
        )
      )
    ),
    React.createElement(Card, { title: "Optimized Results", className: "flex-3", style: { minWidth: "500px" } },
      React.createElement(ResultsTable, { batches, selectedBatchIndex, onSelectBatch, onDownloadBatch, onDeleteBatch, generating })
    )
  );
};

// ===================== Main Component =====================

const STORAGE_KEY = "reinvent4_batches";

function loadSavedState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const restored = {};
      for (let i = 0; i < 6; i++) {
        const tab = parsed[i] || {};
        restored[i] = {
          generating: false,
          taskId: null,
          batches: tab.batches || [],
          selectedBatchIndex: (tab.batches && tab.batches.length > 0) ? (tab.batches.length - 1) : -1,
        };
      }
      return restored;
    }
  } catch (e) {
    console.warn("Failed to load saved REINVENT4 state:", e);
  }
  return null;
}

function saveStateToDisk(state) {
  try {
    const toSave = {};
    for (let i = 0; i < 6; i++) {
      toSave[i] = { batches: state[i]?.batches || [] };
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    console.warn("Failed to save REINVENT4 state:", e);
  }
}

const Reinvent4Dash = () => {
  const [priors, setPriors] = useState([]);
  const [selectedPrior, setSelectedPrior] = useState(null);
  const [numSmiles, setNumSmiles] = useState(50);
  const [uniqueMols, setUniqueMols] = useState(true);
  const [randomize, setRandomize] = useState(true);
  // Per-tab state: batches accumulate, selectedBatchIndex picks which to view
  const [tabState, setTabState] = useState(() => {
    const saved = loadSavedState();
    return saved || {
      0: { generating: false, taskId: null, batches: [], selectedBatchIndex: -1 },
      1: { generating: false, taskId: null, batches: [], selectedBatchIndex: -1 },
      2: { generating: false, taskId: null, batches: [], selectedBatchIndex: -1 },
      3: { generating: false, taskId: null, batches: [], selectedBatchIndex: -1 },
      4: { generating: false, taskId: null, batches: [], selectedBatchIndex: -1 },
      5: { generating: false, taskId: null, batches: [], selectedBatchIndex: -1 },
    };
  });
  const [activeTab, setActiveTab] = useState(0);

  const updateTab = useCallback((tabIndex, updates) => {
    setTabState(prev => ({
      ...prev,
      [tabIndex]: { ...prev[tabIndex], ...updates }
    }));
  }, []);

  const currentTab = tabState[activeTab];

  // Scaffold params
  const [scaffoldSmiles, setScaffoldSmiles] = useState("");
  const [numScaffold, setNumScaffold] = useState(10);

  // R-Group params
  const [rgroupSmiles, setRgroupSmiles] = useState("");
  const [numRgroup, setNumRgroup] = useState(10);

  // Linker params
  const [linkerSmiles, setLinkerSmiles] = useState("");
  const [numLinker, setNumLinker] = useState(10);

  // Peptide params
  const [peptideInput, setPeptideInput] = useState("");
  const [numPeptide, setNumPeptide] = useState(10);

  // Optimize params
  const [batchSize, setBatchSize] = useState(64);
  const [maxSteps, setMaxSteps] = useState(100);
  const [maxScore, setMaxScore] = useState(0.6);

  const pollIntervalRef = useRef(null);
  const tabStateRef = useRef(tabState);
  tabStateRef.current = tabState;

  // Get filtered priors based on active tab
  const getFilteredPriors = useCallback(() => {
    const categories = ["generate", "scaffold", "rgroup", "linker", "peptide", "optimize"];
    const category = categories[activeTab];
    const modelNames = PRIOR_CATEGORIES[category] || [];
    return priors.filter(p => modelNames.includes(p.value));
  }, [activeTab, priors]);

  // Load priors on mount
  useEffect(() => {
    agent.Reinvent.listPriors()
      .then((data) => {
        const opts = ALL_PRIORS.map((name) => ({ label: name, value: name }));
        setPriors(opts);
        setSelectedPrior(data.priors[0]);
      })
      .catch((err) => console.error("Failed to load priors", err));
  }, []);

  // Auto-select first prior when tab changes
  const filteredPriors = getFilteredPriors();
  useEffect(() => {
    if (filteredPriors.length > 0 && (!selectedPrior || !filteredPriors.some(p => p.value === selectedPrior))) {
      setSelectedPrior(filteredPriors[0].value);
    }
  }, [activeTab, filteredPriors, selectedPrior]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  const startPolling = useCallback((id, tabIndex, batchLabel) => {
    // Clear any existing poll before starting a new one
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    updateTab(tabIndex, { taskId: id, generating: true });
    pollIntervalRef.current = setInterval(async () => {
      try {
        const task = await agent.Reinvent.getTask(id);
        if (task.status === "completed") {
          const state = tabStateRef.current;
          const currentBatches = state[tabIndex]?.batches || [];
          const newBatch = {
            id: Date.now(),
            label: batchLabel || `Batch #${(currentBatches.length + 1)}`,
            timestamp: new Date().toLocaleString(),
            results: task.result || [],
          };
          setTabState(prev => {
            const tab = prev[tabIndex];
            const newBatches = [...(tab.batches || []), newBatch];
            const newState = {
              ...prev,
              [tabIndex]: { ...tab, batches: newBatches, selectedBatchIndex: newBatches.length - 1, generating: false, taskId: null },
            };
            // Persist to localStorage immediately
            saveStateToDisk(newState);
            return newState;
          });
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        } else if (task.status === "failed") {
          console.error("Task failed:", task.error);
          updateTab(tabIndex, { generating: false, taskId: null });
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      } catch (err) {
        console.error("Poll failed:", err);
      }
    }, 2000);
  }, [updateTab]);

  const handleGenerate = useCallback(async () => {
    // De Novo generation (Reinvent RNN) — no seed SMILES needed
    try {
      const params = {
        num_smiles: numSmiles, prior: selectedPrior,
        unique_molecules: uniqueMols, randomize_smiles: randomize,
      };
      const task = await agent.Reinvent.generate(params);
      startPolling(task.task_id, 0, `${selectedPrior} (${numSmiles} SMILES)`);
    } catch (err) {
      console.error("Generate failed:", err);
      updateTab(0, { generating: false });
    }
  }, [numSmiles, selectedPrior, uniqueMols, randomize, startPolling, updateTab]);

  const handleScaffold = useCallback(async () => {
    // Scaffold Hopping (Mol2Mol) — uses /generate + smiles_input
    try {
      const smilesList = scaffoldSmiles.split("\n").map(s => s.trim()).filter(s => s.length > 0);
      if (smilesList.length === 0) return;
      const params = { num_smiles: numScaffold, prior: selectedPrior, smiles_input: smilesList };
      const task = await agent.Reinvent.generate(params);
      startPolling(task.task_id, 1, `${selectedPrior} (${numScaffold}/scaffold_hop)`);
    } catch (err) {
      console.error("Scaffold failed:", err);
      updateTab(1, { generating: false });
    }
  }, [scaffoldSmiles, selectedPrior, numScaffold, startPolling, updateTab]);

  const handleRGroup = useCallback(async () => {
    // R-Group (LibInvent) — uses /scaffold endpoint
    try {
      const smilesList = rgroupSmiles.split("\n").map(s => s.trim()).filter(s => s.length > 0);
      if (smilesList.length === 0) return;
      const params = { smiles: smilesList, prior: selectedPrior, num_smiles: numRgroup };
      const task = await agent.Reinvent.scaffold(params);
      startPolling(task.task_id, 2, `${selectedPrior} (${numRgroup}/rgroup)`);
    } catch (err) {
      console.error("RGroup failed:", err);
      updateTab(2, { generating: false });
    }
  }, [rgroupSmiles, selectedPrior, numRgroup, startPolling, updateTab]);

  const handleLinker = useCallback(async () => {
    // Linker Design (LinkInvent) — uses /generate + smiles_input
    try {
      const smilesList = linkerSmiles.trim() ? linkerSmiles.split("\n").map(s => s.trim()).filter(s => s.length > 0) : undefined;
      if (!smilesList || smilesList.length === 0) return;
      const params = { num_smiles: numLinker, prior: selectedPrior, smiles_input: smilesList };
      const task = await agent.Reinvent.generate(params);
      startPolling(task.task_id, 3, `${selectedPrior} (${numLinker} linkers)`);
    } catch (err) {
      console.error("Linker generate failed:", err);
      updateTab(2, { generating: false });
    }
  }, [numLinker, selectedPrior, startPolling, updateTab]);

  const handlePeptide = useCallback(async () => {
    // Peptide (PepInvent) — uses /generate + smiles_input
    try {
      const inputList = peptideInput.trim() ? peptideInput.split("\n").map(s => s.trim()).filter(s => s.length > 0) : undefined;
      if (!inputList || inputList.length === 0) return;
      const params = { num_smiles: numPeptide, prior: selectedPrior, smiles_input: inputList };
      const task = await agent.Reinvent.generate(params);
      startPolling(task.task_id, 4, `${selectedPrior} (${numPeptide} peptides)`);
    } catch (err) {
      console.error("Peptide generate failed:", err);
      updateTab(4, { generating: false });
    }
  }, [numPeptide, peptideInput, selectedPrior, startPolling, updateTab]);

  const handleOptimize = useCallback(async () => {
    // Don't clear previous results — batches accumulate
    try {
      const params = {
        prior: selectedPrior,
        batch_size: batchSize,
        max_steps: maxSteps,
        max_score: maxScore,
      };
      const task = await agent.Reinvent.optimize(params);
      startPolling(task.task_id, 5, `${selectedPrior} (batch=${batchSize}, steps=${maxSteps}, score=${maxScore})`);
    } catch (err) {
      console.error("Optimize failed:", err);
      updateTab(5, { generating: false });
    }
  }, [batchSize, maxSteps, maxScore, selectedPrior, startPolling, updateTab]);

  const handleDownload = useCallback((tabIndex, batchIndex) => {
    const batches = tabState[tabIndex]?.batches || [];
    const batch = batches[batchIndex >= 0 ? batchIndex : batches.length - 1];
    if (!batch || batch.results.length === 0) return;
    const results = batch.results;
    let csv = "SMILES,NLL,Formula\n";
    for (const r of results) {
      csv += `${r.SMILES},${r.NLL},"${smilesToFormula(r.SMILES)}"\n`;
    }
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeLabel = (batch.label || "batch").replace(/[^a-zA-Z0-9_]/g, "_");
    a.download = `reinvent4_${safeLabel}_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, "")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [tabState]);

  const handleSelectBatch = useCallback((tabIndex, batchIndex) => {
    setTabState(prev => ({
      ...prev,
      [tabIndex]: { ...prev[tabIndex], selectedBatchIndex: batchIndex },
    }));
  }, []);

  const handleDeleteBatch = useCallback((tabIndex, batchIndex) => {
    setTabState(prev => {
      const tab = prev[tabIndex];
      const newBatches = tab.batches.filter((_, i) => i !== batchIndex);
      // If we deleted the last batch, reset selectedBatchIndex; otherwise clamp it
      const newIdx = newBatches.length === 0 ? -1
        : Math.min(batchIndex, newBatches.length - 1);
      const newState = {
        ...prev,
        [tabIndex]: { ...tab, batches: newBatches, selectedBatchIndex: newIdx },
      };
      saveStateToDisk(newState);
      return newState;
    });
  }, []);

  const onTabChange = useCallback((e) => {
    setActiveTab(e.index);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const defaultPriorTooltip = React.createElement(Message, {
    severity: "info",
    text: "Only relevant models are shown for each tab",
    style: { fontSize: "0.85rem" }
  });

  return React.createElement("div", { className: "flex flex-column w-full fadein animation-duration-500" },
    React.createElement("div", { className: "flex w-full" },
      React.createElement(SectionHeading, { icon: "pi pi-sitemap", heading: "Molecular Design (REINVENT4)" })
    ),

    React.createElement(TabView, { activeIndex: activeTab, onTabChange, className: "w-full" },
      React.createElement(TabPanel, { header: "Generate", leftIcon: "pi pi-play mr-2" },
        React.createElement(GenerateTab, {
          priors: filteredPriors, selectedPrior, setSelectedPrior,
          numSmiles, setNumSmiles, uniqueMols, setUniqueMols, randomize, setRandomize,
          handleGenerate,
          generating: tabState[0].generating, batches: tabState[0].batches || [], selectedBatchIndex: tabState[0].selectedBatchIndex,
          onSelectBatch: (i) => handleSelectBatch(0, i), onDownloadBatch: (i) => handleDownload(0, i),
          onDeleteBatch: (i) => handleDeleteBatch(0, i)
        })
      ),
      React.createElement(TabPanel, { header: "Scaffold Hop", leftIcon: "pi pi-sitemap mr-2", tooltip: "Mol2Mol — scaffold hopping from seed molecules" },
        React.createElement(ScaffoldHoppingTab, {
          priors: filteredPriors, selectedPrior, setSelectedPrior,
          scaffoldSmiles, setScaffoldSmiles, numScaffold, setNumScaffold,
          handleScaffold,
          generating: tabState[1].generating, batches: tabState[1].batches || [], selectedBatchIndex: tabState[1].selectedBatchIndex,
          onSelectBatch: (i) => handleSelectBatch(1, i), onDownloadBatch: (i) => handleDownload(1, i),
          onDeleteBatch: (i) => handleDeleteBatch(1, i)
        })
      ),
      React.createElement(TabPanel, { header: "R-Group", leftIcon: "pi pi-pencil mr-2", tooltip: "LibInvent — replace R-groups on a scaffold" },
        React.createElement(RGroupTab, {
          priors: filteredPriors, selectedPrior, setSelectedPrior,
          rgroupSmiles, setRgroupSmiles, numRgroup, setNumRgroup,
          handleRGroup,
          generating: tabState[2].generating, batches: tabState[2].batches || [], selectedBatchIndex: tabState[2].selectedBatchIndex,
          onSelectBatch: (i) => handleSelectBatch(2, i), onDownloadBatch: (i) => handleDownload(2, i),
          onDeleteBatch: (i) => handleDeleteBatch(2, i)
        })
      ),
      React.createElement(TabPanel, { header: "Linker", leftIcon: "pi pi-link mr-2", tooltip: "LinkInvent — design linkers between warheads" },
        React.createElement(LinkerTab, {
          priors: filteredPriors, selectedPrior, setSelectedPrior,
          linkerSmiles, setLinkerSmiles, numLinker, setNumLinker,
          handleLinker,
          generating: tabState[3].generating, batches: tabState[3].batches || [], selectedBatchIndex: tabState[3].selectedBatchIndex,
          onSelectBatch: (i) => handleSelectBatch(3, i), onDownloadBatch: (i) => handleDownload(3, i),
          onDeleteBatch: (i) => handleDeleteBatch(3, i)
        })
      ),
      React.createElement(TabPanel, { header: "Peptide", leftIcon: "pi pi-star mr-2", tooltip: "PepInvent — design peptide variants" },
        React.createElement(PeptideTab, {
          priors: filteredPriors, selectedPrior, setSelectedPrior,
          numPeptide, setNumPeptide, peptideInput, setPeptideInput,
          handlePeptide,
          generating: tabState[4].generating, batches: tabState[4].batches || [], selectedBatchIndex: tabState[4].selectedBatchIndex,
          onSelectBatch: (i) => handleSelectBatch(4, i), onDownloadBatch: (i) => handleDownload(4, i),
          onDeleteBatch: (i) => handleDeleteBatch(4, i)
        })
      ),
      React.createElement(TabPanel, { header: "Optimize", leftIcon: "pi pi-chart-line mr-2", tooltip: "RL staged learning optimization" },
        React.createElement(OptimizeTab, {
          priors: filteredPriors, selectedPrior, setSelectedPrior,
          batchSize, setBatchSize, maxSteps, setMaxSteps, maxScore, setMaxScore,
          handleOptimize,
          generating: tabState[5].generating, batches: tabState[5].batches || [], selectedBatchIndex: tabState[5].selectedBatchIndex,
          onSelectBatch: (i) => handleSelectBatch(5, i), onDownloadBatch: (i) => handleDownload(5, i),
          onDeleteBatch: (i) => handleDeleteBatch(5, i)
        })
      )
    )
  );
};

export default observer(Reinvent4Dash);
